import React, { useEffect, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  GripHorizontal,
  Layers,
  Star,
  Trash2,
  UploadCloud,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui";
import LazyImage from "../common/LazyImage";
import {
  SUPPORTED_UPLOAD_ACCEPT,
  splitSupportedUploads,
  rejectedUploadsMessage,
} from "../../utils/imageUploadGuard";
import { imageKey, isReordered, orderSig, urlsOf } from "../../utils/propertyImages";

/**
 * The one Manage Gallery dialog for every property page (CRM property,
 * Collings listing, public property). Pages keep their own persistence,
 * which differs by endpoint and refetch rule; this owns the behaviour:
 *
 *  - a reorder (drag, arrows, make-cover) autosaves after a short pause, with
 *    a submitted-order signature so a failed save or a non-1:1 round-trip
 *    can't loop; Retry resends; every close path flushes a pending reorder,
 *    and the dialog stays open while a save is in flight so a newer drag is
 *    never dropped;
 *  - the pending order is only resynced from the server list on a membership
 *    change (add / delete / replace), never on a pure reorder, so a refetch
 *    can't clobber a drag made while a save was in flight;
 *  - the original filename sits in a caption strip under the photo, where it
 *    reads on any image, with the position number beside it (the office
 *    orders photos by the numbers agents put in the file names - DOMA
 *    13342354662);
 *  - a grip badge on each tile says "drag me"; the whole tile is the handle,
 *    and the arrows / star cover trackpads and touch screens.
 *
 * Props:
 *   open, onClose()                 dialog state; onClose is called for Done,
 *                                   the X, Escape and the overlay click
 *   images                          the SAVED list: string URLs or
 *                                   {file, is_floorplan?, original_name?}
 *   onReorder(urls) -> Promise      persist a new order; reject on failure
 *   onAddFiles(files) -> Promise    upload; receives only portal-safe files
 *   onDelete(img) -> Promise
 *   busy                            true while the page is uploading/deleting
 *   onToggleFloorplan(img)          optional; shows the per-photo floor-plan
 *                                   toggle (CRM property page)
 *   onOpenLightbox(img)             optional; shows the eye button
 *   extraTab {label, description, content}
 *                                   optional second tab (the Collings page's
 *                                   Floorplan uploads)
 */

export const AUTOSAVE_DELAY_MS = 600;

const stop = (fn) => (e) => {
  e.stopPropagation();
  fn();
};

function OrderStatus({ saving, failed, unsaved, onRetry }) {
  if (saving) {
    return <span className="text-[11px] text-[#55C5D0] font-medium animate-pulse">Saving order…</span>;
  }
  if (failed) {
    return (
      <span className="text-[11px] text-red-500 font-medium flex items-center gap-2">
        Couldn't save order
        <button type="button" onClick={onRetry} className="underline hover:no-underline">
          Retry
        </button>
      </span>
    );
  }
  if (unsaved) {
    return <span className="text-[11px] text-[#9CA3AF] font-medium">Saving shortly…</span>;
  }
  return <span className="text-[11px] text-[#9CA3AF] font-medium">All changes saved</span>;
}

function GalleryTile({ img, idx, isLast, busy, onMove, onDelete, onToggleFloorplan, onOpenLightbox }) {
  const src = imageKey(img);
  const isFloorplanImg = typeof img === "object" && !!img.is_floorplan;
  const name = (typeof img === "object" && img?.original_name) || "";
  return (
    <>
      <div className="relative aspect-square bg-gray-100">
        <LazyImage src={src} className="w-full h-full object-cover pointer-events-none" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
        {/* Drag affordance. Decorative: the whole tile is the handle. */}
        <span className="absolute top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded bg-black/40 text-white z-10 pointer-events-none">
          <GripHorizontal className="w-4 h-4" />
        </span>
        {idx === 0 ? (
          <span className="absolute top-2 left-2 px-2 py-1 bg-amber-400 text-white text-[10px] font-bold rounded-md flex items-center gap-1 z-10 pointer-events-none">
            <Star className="w-3 h-3 fill-current" />
            Cover
          </span>
        ) : (
          <button
            type="button"
            title="Make cover photo"
            onClick={stop(() => onMove(idx, 0))}
            className="absolute top-2 left-2 p-1.5 bg-white/90 text-amber-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-amber-50 z-10"
          >
            <Star className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          title="Remove photo"
          onClick={stop(() => onDelete(img))}
          disabled={busy}
          className="absolute top-2 right-2 p-1.5 bg-white/90 text-red-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 z-10"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        {onToggleFloorplan && (
          /* Labelled either way, but only the TAGGED plan stays visible all the
             time - a "Mark floor plan" label on every tile drowned out the one
             that mattered. */
          <button
            type="button"
            title={isFloorplanImg ? "Unmark floor plan" : "Shows under REA's Floorplan tab instead of the photo gallery"}
            onClick={stop(() => onToggleFloorplan(img))}
            disabled={busy}
            className={`absolute bottom-2 left-2 px-2 py-1 rounded-md text-[10px] font-bold flex items-center gap-1 z-10 transition-all ${
              isFloorplanImg
                ? "bg-[#55C5D0] text-white hover:bg-[#45b5c0]"
                : "bg-black/60 text-white hover:bg-black/80 opacity-0 group-hover:opacity-100"
            }`}
          >
            <Layers className="w-3 h-3" />
            {isFloorplanImg ? "Floor plan ✓" : "Mark floor plan"}
          </button>
        )}
        {onOpenLightbox && (
          <button
            type="button"
            title="View full size"
            onClick={stop(() => onOpenLightbox(img))}
            className="absolute bottom-2 right-2 p-1.5 bg-white/90 text-[#374151] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-100 z-10"
          >
            <Eye className="w-4 h-4" />
          </button>
        )}
      </div>
      {/* Caption strip: position + the uploader's file name, under the photo so
          it reads on any image. Blank name for photos uploaded before the name
          was captured. */}
      <div className="flex items-center gap-2 px-2 py-1.5 border-t border-gray-100 min-h-[34px]" data-testid="gallery-caption">
        <span className="shrink-0 min-w-[22px] text-center bg-[#374151] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
          {idx + 1}
        </span>
        <span className="flex-1 text-[11px] text-gray-600 truncate" title={name}>
          {name}
        </span>
        <span className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            title="Move left"
            onClick={stop(() => onMove(idx, idx - 1))}
            disabled={idx === 0}
            className="p-1 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:pointer-events-none"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            title="Move right"
            onClick={stop(() => onMove(idx, idx + 1))}
            disabled={isLast}
            className="p-1 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:pointer-events-none"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </span>
      </div>
    </>
  );
}

export default function ManageGalleryDialog({
  open,
  onClose,
  images,
  onReorder,
  onAddFiles,
  onDelete,
  busy = false,
  onToggleFloorplan,
  onOpenLightbox,
  extraTab,
}) {
  const [pending, setPending] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderSaveError, setOrderSaveError] = useState(false);
  const [tab, setTab] = useState("images");
  const inputRef = useRef(null);
  // Signature of the order last SUBMITTED. Autosave skips an order already
  // sent, so neither a failed save nor a backend that doesn't round-trip the
  // order 1:1 can loop; a new reorder re-arms it, Retry bypasses it.
  const lastSavedOrderSig = useRef(null);

  const saved = images || [];
  const shown = pending ?? saved;
  const hasUnsavedOrder = isReordered(pending, saved);

  useEffect(() => {
    if (!open) {
      setPending(null);
      setOrderSaveError(false);
      setTab("images");
      lastSavedOrderSig.current = null;
      return;
    }
    setPending((prev) => {
      const server = [...saved];
      if (!prev) return server;
      const sameMembership =
        prev.length === server.length &&
        prev.every((p) => server.some((s) => imageKey(s) === imageKey(p)));
      return sameMembership ? prev : server;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, images]);

  const saveOrder = async (list) => {
    const urls = urlsOf(list);
    if (!urls.length) return;
    const sig = urls.join("|");
    lastSavedOrderSig.current = sig;
    try {
      setSavingOrder(true);
      setOrderSaveError(false);
      await onReorder(urls);
      // Settle on the server's answer unless a newer drag arrived meanwhile
      // (its signature differs, so autosave will pick it up next).
      setPending((cur) => (orderSig(cur) === sig ? null : cur));
    } catch (err) {
      // Keep the pending order so Retry can resend it; autosave won't, thanks
      // to the signature. The toast survives a closed dialog.
      setOrderSaveError(true);
      toast.error(err?.response?.data?.detail || err?.message || "Couldn't save the new photo order");
    } finally {
      setSavingOrder(false);
    }
  };

  useEffect(() => {
    if (!open || !hasUnsavedOrder || savingOrder) return undefined;
    if (orderSig(pending) === lastSavedOrderSig.current) return undefined;
    const timer = setTimeout(() => saveOrder(pending), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasUnsavedOrder, savingOrder, pending]);

  const move = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= shown.length || fromIdx === toIdx) return;
    const next = [...shown];
    const [removed] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, removed);
    setOrderSaveError(false);
    // Dragged back to the saved order: forget the last submission, so dragging
    // away again is a fresh change rather than "already sent".
    if (orderSig(next) === orderSig(saved)) lastSavedOrderSig.current = null;
    setPending(next);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    move(result.source.index, result.destination.index);
  };

  // Flush a pending reorder, then close. While a save is in flight the dialog
  // stays open (Done is disabled; Escape / overlay are ignored here) so a drag
  // made during that save can't be lost and a late failure can't land on a
  // reset dialog.
  const close = () => {
    if (savingOrder) return;
    if (hasUnsavedOrder && orderSig(pending) !== lastSavedOrderSig.current) saveOrder(pending);
    onClose();
  };

  const handlePick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = "";
    if (!picked.length) return;
    const { ok, rejected } = splitSupportedUploads(picked);
    if (rejected.length) toast.error(rejectedUploadsMessage(rejected));
    if (!ok.length) return;
    await onAddFiles(ok);
  };

  const intro =
    `Add, remove, or reorder property images. Drag a photo to move it, use the arrows to nudge it one step, or the star to make it the cover photo - the current cover carries the amber Cover tag.${onOpenLightbox ? " The eye opens the full-size photo." : ""}${onToggleFloorplan ? ' "Mark floor plan" (max 2) sends a photo to REA\'s own Floorplan tab instead of the gallery.' : ""} Order changes save automatically.`;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
      {/* Width via style, not className: DialogContent's own template string
          ends with sm:max-w-lg and, with no tailwind-merge, beats a max-w-*
          utility passed in. */}
      <DialogContent style={{ maxWidth: "min(95vw, 1400px)" }} className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Gallery</DialogTitle>
          <DialogDescription>
            {tab === "images" ? intro : extraTab?.description}
          </DialogDescription>
        </DialogHeader>

        {extraTab && (
          <div className="flex items-center gap-2 mt-4">
            {[["images", "Images"], ["extra", extraTab.label]].map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={`px-4 py-1.5 rounded-full text-[11px] font-bold border transition-colors ${
                  tab === id
                    ? "bg-[#374151] text-white border-[#374151]"
                    : "bg-white text-[#374151] border-gray-300 hover:border-[#55C5D0]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {tab === "extra" && extraTab ? (
          <div className="mt-6">{extraTab.content}</div>
        ) : (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3 gap-3">
              <p className="text-[11px] text-[#374151] opacity-70 font-medium">
                The file name under each photo is the one it was uploaded with.
              </p>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-[#55C5D0]/60 transition-all group shrink-0 disabled:opacity-50 disabled:cursor-wait"
              >
                <UploadCloud className="w-4 h-4 text-gray-400 group-hover:text-[#55C5D0]" />
                <span className="text-[11px] font-bold text-gray-500 group-hover:text-[#55C5D0]">
                  {busy ? "Working…" : "Add Images"}
                </span>
                <input
                  type="file"
                  ref={inputRef}
                  className="hidden"
                  multiple
                  accept={SUPPORTED_UPLOAD_ACCEPT}
                  onChange={handlePick}
                  data-testid="gallery-file-input"
                />
              </button>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="gallery-images" direction="horizontal">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                  >
                    {shown.map((img, idx) => {
                      const stableKey = imageKey(img) || `img-${idx}`;
                      return (
                        <Draggable key={stableKey} draggableId={stableKey} index={idx}>
                          {(dragProvided, snapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              className={`relative group rounded-xl overflow-hidden border-2 bg-white transition-all ${
                                snapshot.isDragging
                                  ? "opacity-90 border-[#55C5D0] shadow-xl z-50 scale-105"
                                  : "border-gray-200 hover:border-[#55C5D0]/70 cursor-grab active:cursor-grabbing"
                              }`}
                            >
                              <GalleryTile
                                img={img}
                                idx={idx}
                                isLast={idx === shown.length - 1}
                                busy={busy}
                                onMove={move}
                                onDelete={onDelete}
                                onToggleFloorplan={onToggleFloorplan}
                                onOpenLightbox={onOpenLightbox}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {shown.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <UploadCloud className="w-10 h-10 text-gray-300 mb-2" />
                <p className="text-[#374151] opacity-50 text-sm font-medium">No images yet. Click "Add Images" to upload.</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-6 flex items-center gap-3">
          {tab === "images" && (
            <OrderStatus
              saving={savingOrder}
              failed={orderSaveError}
              unsaved={hasUnsavedOrder}
              onRetry={() => saveOrder(pending)}
            />
          )}
          <Button onClick={close} variant="outline" disabled={savingOrder}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
